import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import CollapsibleWidget from './CollapsibleWidget';
import './RequirementsWidget.css';
import { translateError } from '../utils/errorMessages';

interface RequirementStats {
  requirementTypeId: string;
  name: string;
  code: string;
  isRequired: boolean;
  dueDate?: string;
  stats: {
    completed: number;
    pending: number;
    missing: number;
  };
}

interface RequirementsOverview {
  eventId: string;
  totalRequirements: number;
  requirements: RequirementStats[];
  membersSummary: {
    total: number;
    allComplete: number;
    somePending: number;
    someMissing: number;
  };
}

interface RequirementsWidgetProps {
  eventId: string;
}

const RequirementsWidget: React.FC<RequirementsWidgetProps> = ({ eventId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<RequirementsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRequirements, setExpandedRequirements] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (eventId) {
      fetchRequirementsOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchRequirementsOverview = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: overview } = await apiClient.get<RequirementsOverview>(
        `/api/events/${eventId}/requirements-overview`
      );

      setData(overview);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const toggleRequirement = (reqId: string) => {
    setExpandedRequirements(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reqId)) {
        newSet.delete(reqId);
      } else {
        newSet.add(reqId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <CollapsibleWidget
        title={t('requirements.widgetTitle')}
        defaultExpanded={true}
        storageKey={`event-${eventId}-requirements`}
      >
        <div className="requirements-widget loading">{t('common.loading')}</div>
      </CollapsibleWidget>
    );
  }

  if (error) {
    return (
      <CollapsibleWidget
        title={t('requirements.widgetTitle')}
        defaultExpanded={true}
        storageKey={`event-${eventId}-requirements`}
      >
        <div className="requirements-widget error">{t('common.error')}: {error}</div>
      </CollapsibleWidget>
    );
  }

  if (!data || data.totalRequirements === 0) {
    return (
      <CollapsibleWidget
        title={t('requirements.widgetTitle')}
        defaultExpanded={false}
        storageKey={`event-${eventId}-requirements`}
      >
        <div className="requirements-widget empty">
          {t('requirements.noRequirementsConfigured')}
        </div>
      </CollapsibleWidget>
    );
  }

  const { membersSummary, requirements } = data;

  return (
    <CollapsibleWidget
      title={t('requirements.widgetTitle')}
      defaultExpanded={true}
      storageKey={`event-${eventId}-requirements`}
    >
      <div className="requirements-widget">
        {/* Summary Section */}
        <div className="requirements-summary">
          <div className="summary-item summary-complete">
            <span className="summary-count">{membersSummary.allComplete}</span>
            <span className="summary-label">{t('requirements.complete')}</span>
          </div>
          <div className="summary-item summary-pending">
            <span className="summary-count">{membersSummary.somePending}</span>
            <span className="summary-label">{t('requirements.pending')}</span>
          </div>
          <div className="summary-item summary-missing">
            <span className="summary-count">{membersSummary.someMissing}</span>
            <span className="summary-label">{t('requirements.missing')}</span>
          </div>
        </div>

        {/* Requirements List */}
        <div className="requirements-breakdown">
          <div className="breakdown-title">{t('requirements.breakdown')}</div>
          {requirements.map(req => (
            <div key={req.requirementTypeId} className="requirement-row">
              <button
                type="button"
                className="requirement-row-header"
                onClick={() => toggleRequirement(req.requirementTypeId)}
              >
                <div className="requirement-row-info">
                  <span className="requirement-row-name">
                    {req.name}
                    {req.isRequired && <span className="required-indicator">*</span>}
                  </span>
                  {req.dueDate && (
                    <span className="requirement-row-due">
                      {t('requirements.due')}: {new Date(req.dueDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="requirement-row-stats">
                  <span className="stat-complete" title={t('requirements.complete')}>
                    {req.stats.completed}
                  </span>
                  <span className="stat-pending" title={t('requirements.pending')}>
                    {req.stats.pending}
                  </span>
                  <span className="stat-missing" title={t('requirements.missing')}>
                    {req.stats.missing}
                  </span>
                  <span className={`expand-icon ${expandedRequirements.has(req.requirementTypeId) ? 'expanded' : ''}`}>
                    ▼
                  </span>
                </div>
              </button>

              {expandedRequirements.has(req.requirementTypeId) && (
                <div className="requirement-row-details">
                  <div className="detail-row">
                    <span className="detail-label">{t('requirements.status.approved')}:</span>
                    <span className="detail-value complete">{req.stats.completed}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">{t('requirements.pendingReview')}:</span>
                    <span className="detail-value pending">{req.stats.pending}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">{t('requirements.notSubmitted')}:</span>
                    <span className="detail-value missing">{req.stats.missing}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </CollapsibleWidget>
  );
};

export default RequirementsWidget;
